import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Actions as DASHBOARD_ACTIONS } from '../../store/actions/consultantDashboardActions';
import { toggleAlertMessage } from '../../store/action';
import WorkItemFileDrop from './WorkItemFileDrop';
import WorkItemAttachmentView from './WorkItemAttachmentView';
import { pendingBlob, pendingMeta } from './workItemFileItems';

const clientOpts = { client: true };

const putSignedFiles = async (rows, files) => {
  await Promise.all(
    rows.map((row, i) =>
      fetch(row.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': pendingMeta(files[i]).contentType },
        body: pendingBlob(files[i]),
      }),
    ),
  );
};

/**
 * Client-facing Shared asks (R-7703, R-7713, R-7714).
 */
const SharedAsksPage = ({
  listSharedAsks,
  getSharedAsk,
  updateSharedAsk,
  replySharedAsk,
  createSharedAsk,
  requestAttachmentUpload,
  addWorkItemFiles,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  showAlert,
}) => {
  const history = useHistory();
  const { id: routeId } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createFiles, setCreateFiles] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listSharedAsks();
    if (result?.ok) setItems(result.items || []);
    setLoading(false);
  }, [listSharedAsks]);

  const loadDetail = useCallback(
    async (id) => {
      if (!id) {
        setDetail(null);
        return;
      }
      const result = await getSharedAsk(id);
      if (result?.ok) setDetail(result.item);
      else showAlert(true, result?.message || 'Could not load ask.', 'error', 5000);
    },
    [getSharedAsk, showAlert],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (routeId) loadDetail(routeId);
  }, [routeId, loadDetail]);

  return (
    <div className='p-6 flex flex-col gap-4'>
      <div className='flex items-center gap-2'>
        {routeId && (
          <Button variant='ghost' size='sm' onClick={() => history.push('/shared-asks')}>
            <ArrowLeft size={16} strokeWidth={1.5} />
            All asks
          </Button>
        )}
        <div>
          <h1 className='text-xl font-semibold text-foreground'>Shared asks</h1>
          <p className='text-sm text-muted-foreground'>Work your consultant has shared with you</p>
        </div>
      </div>

      {!routeId && (
        <>
          <Card className='bg-card'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm'>Raise a query</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2'>
              <Input
                placeholder='Title'
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <textarea
                className='w-full min-h-[64px] rounded-md border border-border bg-input px-3 py-2 text-sm'
                placeholder='What do you need help with?'
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
              <WorkItemFileDrop files={createFiles} onChange={setCreateFiles} />
              <Button
                size='sm'
                disabled={!newTitle.trim()}
                onClick={async () => {
                  const created = await createSharedAsk({
                    title: newTitle.trim(),
                    description: newDescription.trim() || null,
                  });
                  if (!created?.ok) {
                    showAlert(true, created?.message || 'Could not create query.', 'error', 5000);
                    return;
                  }
                  let item = created.item;
                  if (createFiles.length) {
                    const meta = await requestAttachmentUpload(
                      item.id,
                      {
                        kind: 'files',
                        files: createFiles.map(pendingMeta),
                      },
                      clientOpts,
                    );
                    if (!meta?.ok) {
                      showAlert(true, meta?.message || 'Upload prepare failed.', 'error', 5000);
                    } else {
                      try {
                        await putSignedFiles(meta.attachments, createFiles);
                        const attached = await addWorkItemFiles(
                          item.id,
                          meta.attachments.map(({ uploadUrl, ...rest }) => rest),
                          clientOpts,
                        );
                        if (attached?.ok) item = attached.item;
                      } catch (err) {
                        showAlert(true, 'File upload failed.', 'error', 5000);
                      }
                    }
                  }
                  setNewTitle('');
                  setNewDescription('');
                  setCreateFiles([]);
                  refresh();
                  history.push(`/shared-asks/${item.id}`);
                }}>
                Send query
              </Button>
            </CardContent>
          </Card>

          <Card className='bg-card'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm'>Open asks</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className='flex justify-center py-8'>
                  <Spinner />
                </div>
              ) : items.length === 0 ? (
                <p className='text-sm text-muted-foreground'>Nothing shared yet</p>
              ) : (
                <ul className='space-y-2'>
                  {items.map((item) => (
                    <li key={item.id}>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-auto w-full justify-start text-left py-3'
                        onClick={() => history.push(`/shared-asks/${item.id}`)}>
                        <span className='flex w-full flex-col gap-1'>
                          <span className='flex justify-between gap-2'>
                            <span className='font-medium'>{item.title}</span>
                            <Badge variant='outline'>{item.status}</Badge>
                          </span>
                          <span className='text-xs text-muted-foreground'>
                            Due {item.dueDate || 'none'}
                            {item.projectName ? ` · ${item.projectName}` : ''}
                          </span>
                        </span>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {routeId && detail && (
        <Card className='bg-card'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-base'>{detail.title}</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            {detail.description && <p className='text-sm'>{detail.description}</p>}
            <div className='flex flex-wrap gap-2 text-sm'>
              <Badge variant='outline'>{detail.status}</Badge>
              <span className='text-muted-foreground'>Due {detail.dueDate || 'none'}</span>
            </div>

            {(detail.files || []).length ? (
              <div className='space-y-1'>
                <p className='text-xs font-medium text-muted-foreground'>Files</p>
                {(detail.files || []).map((file) => (
                  <WorkItemAttachmentView
                    key={file.id}
                    workItemId={detail.id}
                    attachment={file}
                    downloadWorkItemAttachment={downloadWorkItemAttachment}
                    removeWorkItemAttachment={removeWorkItemAttachment}
                    renameWorkItemAttachment={renameWorkItemAttachment}
                    showAlert={showAlert}
                    onItemUpdated={setDetail}
                    client
                  />
                ))}
              </div>
            ) : null}

            <div className='space-y-2'>
              <p className='text-xs font-medium text-muted-foreground'>Conversation</p>
              <div className='max-h-56 overflow-y-auto space-y-2'>
                {(detail.timeline || [])
                  .filter((e) => e.kind === 'message' || e.kind === 'nudge_sent' || e.kind === 'file_uploaded')
                  .map((e) => (
                    <div key={e.id} className='rounded border border-border p-2 text-sm'>
                      <div className='text-xs text-muted-foreground'>{String(e.at || '').slice(0, 16)}</div>
                      <p>{e.body}</p>
                      {(e.attachments || []).map((att) => (
                        <div key={att.id} className='mt-1'>
                          <WorkItemAttachmentView
                            workItemId={detail.id}
                            attachment={att}
                            downloadWorkItemAttachment={downloadWorkItemAttachment}
                            removeWorkItemAttachment={removeWorkItemAttachment}
                            renameWorkItemAttachment={renameWorkItemAttachment}
                            showAlert={showAlert}
                            onItemUpdated={setDetail}
                            client
                            compact
                          />
                        </div>
                      ))}
                    </div>
                  ))}
              </div>
              <textarea
                className='w-full min-h-[72px] rounded-md border border-border bg-input px-3 py-2 text-sm'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder='Reply…'
              />
              <WorkItemFileDrop files={replyFiles} onChange={setReplyFiles} />
              <div className='flex flex-wrap gap-2'>
                <Button
                  size='sm'
                  onClick={async () => {
                    if (!message.trim() && !replyFiles.length) return;
                    let attachments = null;
                    if (replyFiles.length) {
                      const meta = await requestAttachmentUpload(
                        detail.id,
                        {
                          kind: 'message',
                          files: replyFiles.map(pendingMeta),
                        },
                        clientOpts,
                      );
                      if (!meta?.ok) {
                        showAlert(true, meta?.message || 'Upload prepare failed.', 'error', 5000);
                        return;
                      }
                      try {
                        await putSignedFiles(meta.attachments, replyFiles);
                      } catch (err) {
                        showAlert(true, 'File upload failed.', 'error', 5000);
                        return;
                      }
                      attachments = meta.attachments.map(({ uploadUrl, ...rest }) => rest);
                    }
                    const result = await replySharedAsk(
                      detail.id,
                      message.trim() || pendingMeta(replyFiles[0]).name,
                      attachments,
                    );
                    if (result?.ok) {
                      setMessage('');
                      setReplyFiles([]);
                      setDetail(result.item);
                    } else {
                      showAlert(true, result?.message || 'Reply failed.', 'error', 5000);
                    }
                  }}>
                  <Send size={14} strokeWidth={1.5} />
                  Reply
                </Button>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={async () => {
                    const result = await updateSharedAsk(detail.id, { done: true });
                    if (result?.ok) {
                      setDetail(result.item);
                      showAlert(true, 'Marked complete — waiting on consultant confirmation.', 'success', 4000);
                      refresh();
                    } else {
                      showAlert(true, result?.message || 'Could not mark complete.', 'error', 5000);
                    }
                  }}>
                  Mark done
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  listSharedAsks: () => dispatch(DASHBOARD_ACTIONS.listSharedAsks()),
  getSharedAsk: (id) => dispatch(DASHBOARD_ACTIONS.getSharedAsk(id)),
  updateSharedAsk: (id, patch) => dispatch(DASHBOARD_ACTIONS.updateSharedAsk(id, patch)),
  replySharedAsk: (id, body, attachments) =>
    dispatch(DASHBOARD_ACTIONS.replySharedAsk(id, body, attachments)),
  createSharedAsk: (body) => dispatch(DASHBOARD_ACTIONS.createSharedAsk(body)),
  requestAttachmentUpload: (id, meta, opts) =>
    dispatch(DASHBOARD_ACTIONS.requestAttachmentUpload(id, meta, opts)),
  addWorkItemFiles: (id, files, opts) => dispatch(DASHBOARD_ACTIONS.addWorkItemFiles(id, files, opts)),
  downloadWorkItemAttachment: (id, attachmentId, opts) =>
    dispatch(DASHBOARD_ACTIONS.downloadWorkItemAttachment(id, attachmentId, opts)),
  removeWorkItemAttachment: (id, attachmentId, opts) =>
    dispatch(DASHBOARD_ACTIONS.removeWorkItemAttachment(id, attachmentId, opts)),
  renameWorkItemAttachment: (id, attachmentId, name, opts) =>
    dispatch(DASHBOARD_ACTIONS.renameWorkItemAttachment(id, attachmentId, name, opts)),
  showAlert: (...args) => dispatch(toggleAlertMessage(...args)),
});

export default connect(null, mapDispatchToProps)(SharedAsksPage);
